import { normalizeTrackingHost } from '@/utils/eventTracking'

function normalizeConfiguredValueList(value) {
  if (Array.isArray(value)) {
    return value
      .flatMap(item => normalizeConfiguredValueList(item))
      .filter(Boolean)
  }

  if (value && typeof value === 'object') {
    return []
  }

  if (value === undefined || value === null) {
    return []
  }

  return String(value)
    .split(',')
    .map(item => item.trim())
    .filter(Boolean)
}

export function resolveConfiguredTrackingValues(configValue, hosts = []) {
  const directValues = normalizeConfiguredValueList(configValue)
  if (directValues.length > 0) {
    return [...new Set(directValues)]
  }

  if (!configValue || typeof configValue !== 'object' || Array.isArray(configValue)) {
    return []
  }

  const normalizedHosts = hosts
    .map(host => normalizeTrackingHost(host))
    .filter(Boolean)

  for (const host of normalizedHosts) {
    const directMatchValues = normalizeConfiguredValueList(configValue[host])
    if (directMatchValues.length > 0) {
      return [...new Set(directMatchValues)]
    }

    const matchedEntry = Object.entries(configValue).find(([key]) => (
      normalizeTrackingHost(key) === host
    ))
    const matchedValues = normalizeConfiguredValueList(matchedEntry?.[1])
    if (matchedValues.length > 0) {
      return [...new Set(matchedValues)]
    }
  }

  return []
}

export function normalizeGoogleTagIds(values) {
  return [...new Set(normalizeConfiguredValueList(values)
    .map((id) => {
      const conversionDestination = String(id).match(/^(AW-\d+)\/[A-Z0-9_-]+$/i)
      return conversionDestination ? conversionDestination[1] : id
    }))]
    .filter(id => /^(?:AW-\d+|DC-\d+|G-[A-Z0-9]+|GT-[A-Z0-9]+|UA-\d+-\d+)$/i.test(id))
}

export function normalizeGoogleAdsConversionDestinations(values) {
  return [...new Set(normalizeConfiguredValueList(values))]
    .filter(destination => /^AW-\d+\/[A-Z0-9_-]+$/i.test(destination))
}

export function buildGoogleAdsConversionDestinations({ sendTo, conversionLabels, tagIds } = {}) {
  const directDestinations = normalizeGoogleAdsConversionDestinations(sendTo)
  const normalizedLabels = normalizeConfiguredValueList(conversionLabels)
  const normalizedTagIds = normalizeGoogleTagIds(tagIds)
    .filter(id => /^AW-\d+$/i.test(id))
  const derivedDestinations = normalizedLabels.flatMap((label) => {
    if (/^AW-\d+\/[A-Z0-9_-]+$/i.test(label)) {
      return [label]
    }

    if (!/^[A-Z0-9_-]+$/i.test(label)) {
      return []
    }

    return normalizedTagIds.map(id => `${id}/${label}`)
  })

  return [...new Set([...directDestinations, ...derivedDestinations])]
}

export function buildGoogleTagInitScript(ids) {
  const normalizedIds = normalizeGoogleTagIds(ids)
  if (normalizedIds.length === 0) {
    return ''
  }

  const configCommands = normalizedIds
    .map(id => `gtag('config', ${JSON.stringify(id)});`)
    .join('\n')

  return [
    `window.__configuredGoogleTagIds = ${JSON.stringify(normalizedIds)};`,
    'window.dataLayer = window.dataLayer || [];',
    'function gtag(){dataLayer.push(arguments);}',
    "gtag('js', new Date());",
    configCommands,
  ].join('\n')
}
